import { createContext, ReactNode, useRef, useState } from "react";
import { LoginSchema, LoginType, RoomResponseSchema, RoomResponseType } from "../types/Chat";
import axios from "../axios";
import { ApiCallType } from "../types/General";
import { CHAT_BASE_URL } from "../StaticData";
import useApiCall from "../hooks/useApiCall";

const ChatContext = createContext({
    loading: false,
    error: '',

    loginData: null as (LoginType | null),
    rooms: null as (RoomResponseType | null),

    setLoading: (loading: boolean) => {},
    fetchRooms: (silentUpdate?: boolean) => {},
    manuallyUpdateRooms: (newRooms: RoomResponseType) => {},
    chatLogin: async (FCMToken: string, forceRefresh?: boolean) => {}
})

type ChatContextProviderProps = {
    children: ReactNode;
}

export function ChatContextProvider({ children }: ChatContextProviderProps) {
    const [loginData, setLoginData] = useState<LoginType | null>(null);
    const [rooms, setRooms] = useState<RoomResponseType | null>(null);

    const loginControllerRef = useRef(new AbortController());
    const lastFCMTokenAssigned = useRef<string | undefined>(undefined);

    const requestDataRef = useRef<ApiCallType>({ 
        deep: true, 
        key: 'rooms', 
        fallbackData: [], 
        customDataKey: 'Data',
        avoidCancelPreviosRequest: true,
        schemaValidator: RoomResponseSchema, 
        url: CHAT_BASE_URL + "room/?limit=500", 

        onSuccess: (res) => {
            setError('');
            setLoading(false);
            setRooms(res as RoomResponseType)
        },

        onError: () => {
            setLoading(false);
            setError('مشکلی در دریافت لیست روم ها پیش آمده. دوباره تلاش کنید')
        },
    });

    const [error, setError] = useState('');
    const [roomsRequest, setRoomsRequest] = useState<ApiCallType>({ url: '', key: '', fallbackData: null, schemaValidator: null });
    const { data: roomsResponse, loading, setLoading, setDataManually } = useApiCall<RoomResponseType>(roomsRequest);

    async function chatLogin(FCMToken: string, forceRefresh?: boolean) {
        if ((FCMToken !== '' && lastFCMTokenAssigned.current === '') || forceRefresh) {
            loginControllerRef.current.abort();
        } else if (loginData || loading) {
            return
        };

        try {
            !loginData && setLoading(true);
            lastFCMTokenAssigned.current = FCMToken;

            const response = await axios.post('v2/chat/login', { token: FCMToken });
            const processedData = LoginSchema.parse(response.data);

            setLoginData(processedData);
        } catch (e) {
            setError('خطا در اتصال به سرویس چت. برای تلاش مجدد کلیک کنید');
        } finally {
            setLoading(false);
        }
    }

    function fetchRooms(silentUpdate?: boolean) {
        if (!loginData || loading) return;

        setRoomsRequest({ 
            ...requestDataRef.current,

            silentUpdate: !!rooms,
            invalidateCache: true, 
            refreshSingal: Date.now(), 
            headers: { 'Authorization': 'Bearer ' + loginData.token }
        });
    }

    function manuallyUpdateRooms(newRooms: RoomResponseType) {
        setRooms(newRooms);  
        setDataManually(newRooms);
    }

    const context = {
        loading,
        error,

        loginData,
        rooms,

        chatLogin,
        setLoading,
        fetchRooms,
        manuallyUpdateRooms
    }

    return (
        <ChatContext.Provider value={context}>
            {children}
        </ChatContext.Provider>
    )
}

export default ChatContext;